import type { MenuItem } from '@/components/maia/types'
import { inferAllergens } from './allergens'

export type MenuItemCategory = 'starter' | 'dessert'

export interface StoredMenuItem extends MenuItem {
  id: string
  category: MenuItemCategory
  createdAt: number
  updatedAt?: number
}

function storageKey(category: MenuItemCategory): string {
  return `maia-${category}s`
}

function seededKey(category: MenuItemCategory): string {
  return `maia-${category}s-seeded`
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function readItems(category: MenuItemCategory): StoredMenuItem[] {
  try {
    const raw = localStorage.getItem(storageKey(category))
    return raw ? (JSON.parse(raw) as StoredMenuItem[]) : []
  } catch {
    return []
  }
}

function writeItems(category: MenuItemCategory, items: StoredMenuItem[]): void {
  localStorage.setItem(storageKey(category), JSON.stringify(items))
}

export function getMenuItems(category: MenuItemCategory): StoredMenuItem[] {
  return readItems(category)
}

export function saveMenuItem(
  data: Omit<StoredMenuItem, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }
): StoredMenuItem {
  const items = readItems(data.category)
  const now = Date.now()

  if (data.id) {
    const index = items.findIndex((item) => item.id === data.id)
    if (index === -1) {
      const created: StoredMenuItem = {
        ...data,
        id: data.id,
        createdAt: now,
        updatedAt: now,
      }
      writeItems(data.category, [created, ...items])
      return created
    }

    const updated: StoredMenuItem = {
      ...items[index],
      ...data,
      updatedAt: now,
    }
    items[index] = updated
    writeItems(data.category, items)
    return updated
  }

  const created: StoredMenuItem = {
    ...data,
    id: generateId(),
    createdAt: now,
    updatedAt: now,
  }
  writeItems(data.category, [created, ...items])
  return created
}

export function deleteMenuItem(category: MenuItemCategory, id: string): boolean {
  const items = readItems(category)
  const next = items.filter((item) => item.id !== id)
  if (next.length === items.length) return false
  writeItems(category, next)
  return true
}

export function clearMenuItems(category: MenuItemCategory): void {
  localStorage.removeItem(storageKey(category))
  localStorage.removeItem(seededKey(category))
}

export function setMenuItems(category: MenuItemCategory, items: StoredMenuItem[]): void {
  const now = Date.now()
  writeItems(
    category,
    items.map((item) => ({
      ...item,
      updatedAt: now,
    }))
  )
}

export function seedMenuItems(
  category: MenuItemCategory,
  fallback: MenuItem[]
): void {
  try {
    const alreadySeeded = localStorage.getItem(seededKey(category)) === '1'
    const items = readItems(category)

    const fallbackPriceByTitle = new Map<string, string>(
      fallback
        .filter((item): item is MenuItem & { price: string } => !!item.price)
        .map((item) => [item.title, item.price])
    )

    const fallbackAllergensByTitle = new Map<string, string>(
      fallback
        .filter((item): item is MenuItem & { allergens: string } => !!item.allergens)
        .map((item) => [item.title, item.allergens])
    )

    if (items.length > 0) {
      const needsBackfill = items.some(
        (item) =>
          (!item.price && fallbackPriceByTitle.has(item.title)) ||
          (!item.allergens &&
            (fallbackAllergensByTitle.has(item.title) ||
              inferAllergens(item.title, 'it').length > 0))
      )
      if (needsBackfill) {
        const updated = items.map((item) => ({
          ...item,
          price: item.price || fallbackPriceByTitle.get(item.title) || item.price,
          allergens:
            item.allergens ||
            fallbackAllergensByTitle.get(item.title) ||
            inferAllergens(item.title, 'it') ||
            item.allergens,
        }))
        writeItems(category, updated)
      }
      localStorage.setItem(seededKey(category), '1')
      return
    }

    if (alreadySeeded) return

    const now = Date.now()
    const seeded: StoredMenuItem[] = fallback.map((item) => ({
      id: generateId(),
      category,
      title: item.title,
      description: item.description,
      price: item.price,
      allergens: item.allergens || inferAllergens(item.title, 'it'),
      image: item.image,
      createdAt: now,
    }))

    writeItems(category, seeded)
    localStorage.setItem(seededKey(category), '1')
  } catch {
    // ignore seeding errors
  }
}

export function getStoredItemsOrFallback(
  category: MenuItemCategory,
  fallback: MenuItem[]
): MenuItem[] {
  const items = readItems(category)
  if (items.length === 0) {
    return fallback.map((item) => ({
      ...item,
      allergens: item.allergens || inferAllergens(item.title, 'it'),
    }))
  }

  const fallbackPriceByTitle = new Map<string, string>(
    fallback
      .filter((item): item is MenuItem & { price: string } => !!item.price)
      .map((item) => [item.title, item.price])
  )

  const fallbackAllergensByTitle = new Map<string, string>(
    fallback
      .filter((item): item is MenuItem & { allergens: string } => !!item.allergens)
      .map((item) => [item.title, item.allergens])
  )

  return items.map((item) => ({
    id: item.id,
    title: item.title,
    description: item.description,
    price: item.price || fallbackPriceByTitle.get(item.title) || item.price,
    allergens:
      item.allergens ||
      fallbackAllergensByTitle.get(item.title) ||
      inferAllergens(item.title, 'it') ||
      item.allergens,
    image: item.image,
  }))
}
